import React, { useCallback, useMemo, useReducer } from 'react';
import { useField } from 'react-final-form';
import { observer } from 'mobx-react';
import cm from 'classnames';
import IMask from 'imask';
import { Button } from 'ksnm-common-ui/lib/Button';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { createMask } from 'ksnm-common-ui/lib/MaskedInput';
import { Tooltip } from 'ksnm-common-ui/lib/Tooltip';

import { controlVersionEditStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionEditStore';
import { roundTypeStore } from '@stores/dictionaryStores/roundTypeStore';

import styles from './Rounding.module.scss';

export const Rounding = observer(() => {
  const [open, toggle] = useReducer((state) => !state, false);

  const { isEditMode } = controlVersionEditStore;
  const { roundTypes } = roundTypeStore;

  const {
    input: { value: codeFns },
  } = useField('codeFns');

  const {
    input: { value: delta },
  } = useField('delta');

  const {
    input: { value: roundingType },
  } = useField('roundingType');

  const {
    input: { onChange: changeDecimalQty },
  } = useField('decimalQty');

  const onChangeRoundingType = useCallback(
    (v) => {
      if (!v) changeDecimalQty(null);
    },
    [changeDecimalQty],
  );

  const deltaMask = useMemo(
    () =>
      IMask.createMask({
        mask: Number,
        scale: 5, // Максимальное количество знаков после запятой
        thousandsSeparator: '', // Разделитель тысяч (пустая строка для его отключения, если не нужен)
        padFractionalZeros: false, // Не добавляет нули автоматически после запятой
        normalizeZeros: false, // Приводит незначащие нули в дробной части в порядок (например, 1.00000 -> 1)
        radix: '.', // Символ десятичного разделителя (точка)
        mapToRadix: [','], // Позволяет вводить запятую, которая автоматически заменяется на точку
      }),
    [],
  );

  return (
    <>
      <Button
        onClick={toggle}
        size="middle"
        className={cm(styles.button, { [styles.disabled]: !isEditMode, [styles.fill]: delta || roundingType })}
        icon={<Icon name="book" />}
      >
        <>Округление и дельта</>
      </Button>

      {open && (
        <>
          <div className={styles.field}>
            <TextField
              name="delta"
              label="Допустимая дельта"
              disabled={!isEditMode || codeFns}
              size="small"
              groupStyle={!isEditMode}
              maxLength={255}
              mask={deltaMask}
              unmask
            />

            <div className={styles.tooltip}>
              <Tooltip
                hideOnClick={false}
                content="Если в данное поле ввести число, то при разнице левой и правой частей сравниваемой формулы,
                не превышающей введенное число, шаг контроля будет считаться выполненным успешно. Для контролей сервисов ФНС ввод допустимой дельты невозможен."
              >
                <span>
                  <Icon name="info" />
                </span>
              </Tooltip>
            </div>
          </div>

          <div className={styles.field}>
            <SelectField
              name="roundingType"
              items={roundTypes}
              label="Тип округления"
              disabled={!isEditMode}
              size="small"
              groupStyle={!isEditMode}
              allowClear
              onChange={onChangeRoundingType}
            />
          </div>

          <div className={styles.field}>
            <TextField
              name="decimalQty"
              label="Количество разрядов округления"
              disabled={!isEditMode || !roundingType}
              size="small"
              groupStyle={!isEditMode}
              mask={createMask('NUMBER')}
              unmask
              min={-25}
              max={25}
              required={roundingType}
            />

            <div className={styles.tooltip}>
              <Tooltip
                hideOnClick={false}
                content={
                  <div>
                    <p>
                      Если нужно округлить до 0.1, 0.01, 0.001, то нужно выбрать 1, 2, 3 и т.д., а если до 10, 100,
                      1000, то -1, -2, -3 и т.д.
                    </p>
                    <p>Например, при использовании round:</p>
                    <p>(2) 5471.73 -{'>'} 5471.73</p>
                    <p>(1) 5471.73 -{'>'} 5471.70</p>
                    <p>(0) 5471.73 -{'>'} 5472.00</p>
                    <p>(-1) 5471.73 -{'>'} 5470.00</p>
                    <p>(-2) 5471.73 -{'>'} 5500.00</p>
                  </div>
                }
              >
                <span>
                  <Icon name="info" />
                </span>
              </Tooltip>
            </div>
          </div>
        </>
      )}
    </>
  );
});
